import { defineStore } from "pinia"
import { markRaw } from "vue"
import {
    AccessGroupsApi,
    ActiveAccessGroup
} from "@/ api/AccessGroupsApi"
import {
    JobNotification,
    NotificationsApi,
    NotificationsCount
} from "@/ api/NotificationsApi"
import { chatsStore } from "@/store/chats"
import type {
    Chat,
    ChatHistoryItem,
    ChatLoadState,
    ChatLoadingState,
    ChatMessage,
    ChatModes,
    ChatUser,
    Dialog,
    ResendMessages
} from "@/store/chats"
import { ChatsApi, CreateMessageModel, SearchMessage } from "@/ api/ChatsApi"
import { JobsApi } from "@/ api/JobsApi"
import { jobsStore } from "@/store/jobs"
import { highlightSearchText } from "@/utils/textHelpers"
import { useSignalR } from "@/composables/useSignalR"

export type JobNotificationAction =
    | 'endByWorker'
    | 'sendToChecking'
    | 'sendToReworking'
    | 'endByCreator'
    | 'endByAdmin'

const ACCESS_GROUP_KEY = 'activeAccessGroup'
const SOUND_KEY = 'enableSound'
const HISTORY_LIMIT = 10

const readSoundFromStorage = (): boolean => {
    return localStorage.getItem(SOUND_KEY) !== 'false'
}

const readAccessGroupFromStorage = (): number | null => {
    const raw = localStorage.getItem(ACCESS_GROUP_KEY)
    if (!raw) {
        return null
    }
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : null
}

const makeLoadState = (): ChatLoadingState => ({
    initial: false,
    prev: false,
    next: false,
    canLoadPrev: true,
    canLoadNext: false,
    fetchToken: 0,
    prevToken: 0,
    nextToken: 0,
})

export type HeaderState = {
    accessGroups: ActiveAccessGroup[]
    currentAccessGroupId: number | null
    notificationCount: NotificationsCount
    activeNotifications: JobNotification[]
    historyNotifications: JobNotification[]
    enableSound: boolean

    loadingDialogs: boolean
    chatsAccess: boolean | null
    dialogs: Dialog[]
    chatsById: Record<number, Chat>
    messagesByChatId: Record<number, ChatMessage[]>
    loadingByChatId: Record<number, ChatLoadingState>
    openedChat: number | null
    openedChatsHistory: ChatHistoryItem[]
    searchedMessages: ChatMessage[]
    mode: ChatModes
    chatUsers: ChatUser[] | null
    resendMessages: ResendMessages | null
    editedChat: Chat | null
    pendingCenterMessageId: number | null
}

export const headerStore = defineStore({
    id: 'headerStore',
    state: (): HeaderState => ({
        accessGroups: [],
        currentAccessGroupId: readAccessGroupFromStorage(),
        notificationCount: { JobCount: 0, MessageCount: 0 },
        activeNotifications: [],
        historyNotifications: [],
        enableSound: readSoundFromStorage(),

        loadingDialogs: false,
        chatsAccess: null,
        dialogs: [],
        chatsById: {},
        messagesByChatId: {},
        loadingByChatId: {},
        openedChat: null,
        openedChatsHistory: [],
        searchedMessages: [],
        mode: 'list',
        chatUsers: null,
        resendMessages: null,
        editedChat: null,
        pendingCenterMessageId: null
    }),
    getters: {
        unreadMessagesCount(state): number {
            return state.dialogs.reduce((sum, d) => sum + (d.UnreadCount || 0), 0)
        },
        currentAccessGroup(state): ActiveAccessGroup | null {
            if (state.currentAccessGroupId == null) {
                return null
            }
            return state.accessGroups.find(g => g.Id === state.currentAccessGroupId) ?? null
        },

        isDialogsLoading(state): boolean {
            return state.loadingDialogs
        },
        isChatMessagesLoading(state): boolean {
            return state.openedChat ? !!state.loadingByChatId[state.openedChat]?.initial : false
        },
        loadingNext(state): boolean {
            return state.openedChat ? !!state.loadingByChatId[state.openedChat]?.next : false
        },
        loadingPrev(state): boolean {
            return state.openedChat ? !!state.loadingByChatId[state.openedChat]?.prev : false
        },
        getDialogs(state): Dialog[] {
            return state.dialogs
        },
        getChatMode(state): ChatModes {
            return state.mode
        },
        getOpenedChat(state): Chat | null {
            return state.openedChat
                ? state.chatsById[state.openedChat] || null
                : null
        },
        getOpenedChatId(state): number | null {
            return state.openedChat
        },
        getOpenedChatMessages(state): ChatMessage[] {
            return state.openedChat
                ? state.messagesByChatId[state.openedChat] || []
                : []
        },
        getSearchedMessages(state): ChatMessage[] {
            return state.searchedMessages
        },
        getOpenedChatLoadState(state): ChatLoadState | null {
            if (!state.openedChat) {
                return null
            }
            const ls = state.loadingByChatId[state.openedChat]
            if (!ls) {
                return null
            }
            return {
                id: state.openedChat,
                canLoadPrev: ls.canLoadPrev,
                canLoadNext: ls.canLoadNext,
            }
        },
        getOpenedChatsHistory(state): ChatHistoryItem[] {
            return state.openedChatsHistory
        },
        getChatUsers(state): ChatUser[] | null {
            return state.chatUsers
        },
        getResendMessages(state): ResendMessages | null {
            return state.resendMessages
        },
        getEditedChat(state): Chat | null {
            return state.editedChat
        }
    },
    actions: {
        async loadAccessGroups() {
            const groups = await AccessGroupsApi.getGroupsOfWorker()
            this.accessGroups = groups
            const stored = this.currentAccessGroupId
            const valid = stored != null && groups.some(g => g.Id === stored)
            if (!valid) {
                const first = groups[0]?.Id ?? null
                this.setActiveAccessGroup(first)
            }
        },
        setActiveAccessGroup(id: number | null) {
            this.currentAccessGroupId = id
            if (id == null) {
                localStorage.removeItem(ACCESS_GROUP_KEY)
            } else {
                localStorage.setItem(ACCESS_GROUP_KEY, String(id))
            }
        },

        async loadNotificationCount() {
            try {
                this.notificationCount = await NotificationsApi.getCount()
            } catch (e) {
                console.error('loadNotificationCount', e)
            }
        },
        async refreshAfterSocket() {
            await Promise.all([
                this.loadNotificationCount(),
                this.loadActiveNotifications(0),
            ])
        },
        async loadActiveNotifications(offset = 0) {
            try {
                const list = await NotificationsApi.getActive(offset)
                this.activeNotifications = offset === 0
                    ? list
                    : [...this.activeNotifications, ...list]
            } catch (e) {
                console.error('loadActiveNotifications', e)
            }
        },
        async loadHistoryNotifications(offset = 0) {
            try {
                const list = await NotificationsApi.getHistory(offset)
                this.historyNotifications = offset === 0
                    ? list
                    : [...this.historyNotifications, ...list]
            } catch (e) {
                console.error('loadHistoryNotifications', e)
            }
        },
        async moveNotificationToHistory(id: number) {
            await NotificationsApi.moveToHistory(id)
            const moved = this.activeNotifications.find(n => n.Id === id)
            this.activeNotifications = this.activeNotifications.filter(n => n.Id !== id)
            if (moved) {
                this.historyNotifications = [moved, ...this.historyNotifications]
                if (this.notificationCount.JobCount > 0) {
                    this.notificationCount = {
                        ...this.notificationCount,
                        JobCount: this.notificationCount.JobCount - 1
                    }
                }
            }
        },
        async moveAllToHistory() {
            const moved = this.activeNotifications
            if (!moved.length) {
                return
            }
            await NotificationsApi.moveAllToHistory(moved.map(n => n.Id))
            this.historyNotifications = [...moved, ...this.historyNotifications]
            this.activeNotifications = []
            this.notificationCount = { ...this.notificationCount, JobCount: 0 }

            await Promise.all([this.loadNotificationCount(), this.loadActiveNotifications(0)])
        },
        async applyJobAction(
            notificationId: number,
            jobId: number,
            action: JobNotificationAction,
            message?: string
        ) {
            try {
                let modifiedJob
                switch (action) {
                    case 'endByWorker':
                        modifiedJob = await JobsApi.endByWorker(jobId, message)
                        break
                    case 'sendToChecking':
                        modifiedJob = await JobsApi.sendToChecking(jobId, message!)
                        break
                    case 'sendToReworking':
                        modifiedJob = await JobsApi.sendToReworking(jobId, message!)
                        break
                    case 'endByCreator':
                        modifiedJob = await JobsApi.endByCreator(jobId)
                        break
                    case 'endByAdmin':
                        modifiedJob = await JobsApi.endByAdmin(jobId, message)
                        break
                }
                if (modifiedJob) {
                    const jobs = jobsStore()
                    await jobs.refreshJob(modifiedJob)
                    const lf = jobs.lastFilters
                    await jobs.getJobsCount(lf.textFilter, lf.workerId, lf.tags)
                }
                await this.moveNotificationToHistory(notificationId)
            } catch (e) {
                console.error('applyJobAction', e)
            }
        },

        setSound(value: boolean) {
            this.enableSound = value
            localStorage.setItem(SOUND_KEY, value ? 'true' : 'false')
        },
        toggleSound() {
            this.setSound(!this.enableSound)
        },

        ensureLoadState(chatId: number): ChatLoadingState {
            if (!this.loadingByChatId[chatId]) {
                this.loadingByChatId[chatId] = makeLoadState()
            }
            return this.loadingByChatId[chatId]
        },
        async fetchDialogs() {
            try {
                this.loadingDialogs = true
                if (this.chatsAccess === null) {
                    const access = await ChatsApi.getChatsAccess()
                    this.chatsAccess = access.Allow
                }
                if (!this.chatsAccess) {
                    this.dialogs = []
                    return
                }
                this.dialogs = await ChatsApi.getAllDialogs()
                this.dialogs.forEach(dialog => {
                    const historyItem = this.openedChatsHistory.find(h => h.Id === dialog.Id)
                    if (historyItem && historyItem.Name !== dialog.Name) {
                        historyItem.Name = dialog.Name
                    }
                })
            } catch (error) {
                console.log(error)
            } finally {
                this.loadingDialogs = false
            }
        },
        async fetchChat(chatId: number, forceFetch?: boolean) {
            const loadState = this.ensureLoadState(chatId)
            if (this.chatsById[chatId] && !forceFetch) {
                return
            }

            const myToken = ++loadState.fetchToken
            loadState.prevToken++
            loadState.nextToken++
            loadState.prev = false
            loadState.next = false
            loadState.initial = true
            try {
                const response = await ChatsApi.getChat(chatId)
                if (myToken !== loadState.fetchToken) {
                    return
                }

                const messages = response.Messages || []
                this.chatsById[chatId] = response
                this.messagesByChatId[chatId] = messages.map(msg => markRaw(msg))
                loadState.canLoadPrev = true
                loadState.canLoadNext = false
            } catch (e) {
                console.error(e)
            } finally {
                if (myToken === loadState.fetchToken) {
                    loadState.initial = false
                }
            }
        },
        async fetchChatFromSearch(chatId: number, messageId: number) {
            const loadState = this.ensureLoadState(chatId)
            const myToken = ++loadState.fetchToken
            loadState.prevToken++
            loadState.nextToken++
            loadState.prev = false
            loadState.next = false
            loadState.initial = true
            try {
                const chat = await ChatsApi.getChat(chatId)
                const centered = await ChatsApi.getChatCenteredOnMessage(chatId, messageId)
                if (myToken !== loadState.fetchToken) {
                    return
                }

                this.chatsById[chatId] = chat
                this.messagesByChatId[chatId] = (centered || []).map(msg => markRaw(msg))
                loadState.canLoadPrev = true
                loadState.canLoadNext = true
            } catch (e) {
                console.error(e)
            } finally {
                if (myToken === loadState.fetchToken) {
                    loadState.initial = false
                }
            }
        },
        async fetchChatUsers(personalChatId?: number) {
            this.chatUsers = await ChatsApi.getChatUsers(personalChatId)
        },
        setChatUsers(users: ChatUser[]) {
            this.chatUsers = users
        },
        async createMessage(message: CreateMessageModel, updateDate: string, lastMessageId: number) {
            await ChatsApi.createMessage(message)

            const messages = await ChatsApi.refreshChatMessagesSecond(message.chatId, lastMessageId)
            this.updateChatMessages(message.chatId, messages.Messages)

            // Список диалогов (и обеих панелей) обновит SignalR-эхо MessageSent одним запросом.
            // Если сокет упал — эха не будет, обновляем диалоги активной панели локально.
            if (!useSignalR().isConnected.value) {
                await this.updateDialogInList(updateDate)
            }
        },
        async updateDialogInList(messageDate: string) {
            const dialogs = await ChatsApi.refreshDialogInList(messageDate)
            this.applyDialogList(dialogs)
        },
        applyDialogList(dialogs: Dialog[]) {
            dialogs.forEach(dialog => {
                const historyItem = this.openedChatsHistory.find(h => h.Id === dialog.Id)
                if (historyItem && historyItem.Name !== dialog.Name) {
                    historyItem.Name = dialog.Name
                }
            })
            this.dialogs = dialogs
        },
        async setOpenedChat(chatId: number | null, messageId?: number | null, forceFetch?: boolean) {
            this.openedChat = chatId
            this.pendingCenterMessageId = messageId ?? null

            if (forceFetch && chatId) {
                await this.fetchChat(chatId, forceFetch)
                return
            }

            if (messageId) {
                await this.fetchChatFromSearch(chatId as number, messageId)
            } else if (chatId && !this.chatsById[chatId]) {
                await this.fetchChat(chatId)
            }
        },
        async getPrevMessages(chatId: number, messageDate: string) {
            const loadState = this.ensureLoadState(chatId)
            if (!loadState.canLoadPrev || loadState.prev) {
                return
            }

            const myToken = ++loadState.prevToken
            loadState.prev = true
            try {
                const prevMessages = await ChatsApi.getPreviouseMessages(chatId, messageDate.split('+')[0])
                if (myToken !== loadState.prevToken) {
                    return
                }

                if (prevMessages.length) {
                    const rawMessages = prevMessages.map(msg => markRaw(msg))
                    const existing = this.messagesByChatId[chatId] || []
                    this.messagesByChatId[chatId] = [...rawMessages, ...existing]
                } else {
                    loadState.canLoadPrev = false
                }
            } catch (e) {
                console.error(e)
                loadState.canLoadPrev = false
            } finally {
                if (myToken === loadState.prevToken) {
                    loadState.prev = false
                }
            }
        },
        async getNextMessages(chatId: number, messageDate: string) {
            const loadState = this.ensureLoadState(chatId)
            if (!loadState.canLoadNext || loadState.next) {
                return
            }

            const myToken = ++loadState.nextToken
            loadState.next = true
            try {
                const nextMessages = await ChatsApi.getNextMessages(chatId, messageDate.split('+')[0])
                if (myToken !== loadState.nextToken) {
                    return
                }

                if (nextMessages.length) {
                    const rawMessages = nextMessages.map(msg => markRaw(msg))
                    const existing = this.messagesByChatId[chatId] || []
                    this.messagesByChatId[chatId] = [...existing, ...rawMessages]
                } else {
                    loadState.canLoadNext = false
                }
            } catch (e) {
                console.error(e)
                loadState.canLoadNext = false
            } finally {
                if (myToken === loadState.nextToken) {
                    loadState.next = false
                }
            }
        },
        releaseChatData(chatId: number): void {
            if (this.openedChat === chatId) {
                return
            }
            delete this.messagesByChatId[chatId]
            delete this.chatsById[chatId]
            delete this.loadingByChatId[chatId]
        },
        addOpenedChatsHistory(chat: ChatHistoryItem | SearchMessage): void {
            const id = 'ChatId' in chat ? chat.ChatId : chat.Id
            const name = chat.Name
            const existingIndex = this.openedChatsHistory.findIndex(c => c.Id === id)
            if (existingIndex !== -1) {
                const [item] = this.openedChatsHistory.splice(existingIndex, 1)
                this.openedChatsHistory.push(item)
                return
            }
            this.openedChatsHistory.push({
                Id: id,
                Name: name,
                ScrollTop: null
            })
            while (this.openedChatsHistory.length > HISTORY_LIMIT) {
                const evicted = this.openedChatsHistory.shift()
                if (evicted) {
                    this.releaseChatData(evicted.Id)
                }
            }
        },
        async fetchSearchedMessages(chatId: number, searchText: string) {
            const loadState = this.ensureLoadState(chatId)
            loadState.initial = true
            try {
                const messages = await ChatsApi.searchMessagesInChat(chatId, searchText)
                this.searchedMessages = messages.map(message => ({
                    ...message,
                    Text: highlightSearchText(searchText, message.Text)
                }))
            } catch (e) {
                console.error(e)
            } finally {
                loadState.initial = false
            }
        },
        saveChatScrollPosition(chatId: number, scrollPosition: number) {
            const searchedChatIndex = this.openedChatsHistory.findIndex(chat => chat.Id === chatId)
            if (searchedChatIndex !== -1) {
                this.openedChatsHistory[searchedChatIndex].ScrollTop = scrollPosition
            }
        },
        deleteOpenedChatsHistory(chatId: number): void {
            const searchedChatIndex = this.openedChatsHistory.findIndex(c => c.Id === chatId)
            if (searchedChatIndex !== -1) {
                this.openedChatsHistory.splice(searchedChatIndex, 1)
                if (this.openedChat === chatId) {
                    this.openedChat = null
                }
                this.releaseChatData(chatId)
            }
        },
        setMode(mode: ChatModes): void {
            this.mode = mode
        },
        setCantLoadNewMessages(chatId: number, direction: 'prev' | 'next') {
            const loadState = this.ensureLoadState(chatId)
            if (direction === 'prev') {
                loadState.canLoadPrev = false
            }
            if (direction === 'next') {
                loadState.canLoadNext = false
            }
        },
        updateChatMessages(chatId: number, messages: ChatMessage[]) {
            const existing = this.messagesByChatId[chatId] || []
            const seen = new Set(existing.map(m => m.Id))
            const toAppend = messages.filter(m => !seen.has(m.Id))
            if (toAppend.length) {
                this.messagesByChatId[chatId] = [...existing, ...toAppend.map(m => markRaw(m))]
            }
        },
        async readChatMessage(messageId: number) {
            try {
                await ChatsApi.readMessage(messageId)

                if (this.openedChat) {
                    const list = this.messagesByChatId[this.openedChat]
                    if (list) {
                        const updatedMessageIdx = list.findIndex(msg => msg.Id === messageId)
                        if (updatedMessageIdx !== -1) {
                            const oldMsg = list[updatedMessageIdx]
                            list.splice(updatedMessageIdx, 1, markRaw({ ...oldMsg, Unreaded: false }))
                        }
                    }
                    const updatedDialogIndex = this.dialogs.findIndex(dialog => dialog.Id === this.openedChat)
                    if (updatedDialogIndex !== -1) {
                        this.dialogs[updatedDialogIndex].UnreadCount = this.dialogs[updatedDialogIndex].UnreadCount > 0
                            ? this.dialogs[updatedDialogIndex].UnreadCount - 1
                            : 0
                    }
                }

                try {
                    chatsStore().applyMessageReadFromOther(messageId, this.openedChat)
                } catch (e) {
                    console.error('cross-store sync (readChatMessage) failed', e)
                }
            } catch (e) {
                console.error(e)
            }
        },
        setResendMessages(messages: ResendMessages | null) {
            this.resendMessages = messages
        },
        setEditedChat(chat: Chat | null) {
            this.editedChat = chat
        },

        applyMessageReadFromOther(messageId: number, chatId: number | null) {
            if (!chatId) {
                return
            }
            const list = this.messagesByChatId[chatId]
            if (list) {
                const idx = list.findIndex(msg => msg.Id === messageId)
                if (idx !== -1) {
                    const old = list[idx]
                    list.splice(idx, 1, markRaw({ ...old, Unreaded: false }))
                }
            }
            const dialogIdx = this.dialogs.findIndex(d => d.Id === chatId)
            if (dialogIdx !== -1 && this.dialogs[dialogIdx].UnreadCount > 0) {
                this.dialogs[dialogIdx].UnreadCount -= 1
            }
        },

        applyMessageReadByOther(messageCrossId: number): boolean {
            for (const chatIdStr of Object.keys(this.messagesByChatId)) {
                const chatId = Number(chatIdStr)
                const list = this.messagesByChatId[chatId]
                const idx = list.findIndex(m => m.CrossId === messageCrossId)
                if (idx !== -1) {
                    const old = list[idx]
                    if (old.Unreaded) {
                        list.splice(idx, 1, markRaw({ ...old, Unreaded: false }))
                    }
                    const dialog = this.dialogs.find(d => d.Id === chatId)
                    if (dialog && dialog.Unreaded && !dialog.UnreadCount) {
                        dialog.Unreaded = false
                    }
                    return true
                }
            }
            return false
        },
    }
})
